"use client";

import { motion } from "framer-motion";
import { SUPPORTED_LANGUAGES } from "@/lib/languages";
import { useTranslations, useLocale } from "next-intl";
import { LanguageScroll } from "@/components/ui/language-scroll";
import { LanguageModal } from "@/components/language-modal";
import { useState } from "react";
import * as flags from "country-flag-icons/react/3x2";

// Map language codes to country codes
const getCountryCodeForLanguage = (code: string): string => {
  const languageToCountry: Record<string, string> = {
    "chinese-simplified": "CN",
    "chinese-traditional": "TW",
    cantonese: "HK",
    korean: "KR",
    japanese: "JP",
    thai: "TH",
    vietnamese: "VN",
    indonesian: "ID",
    malay: "MY",
    filipino: "PH",
    hindi: "IN",
    bhojpuri: "IN",
    arabic: "SA",
    persian: "IR",
    hebrew: "IL",
    turkish: "TR",
    english: "GB",
    french: "FR",
    german: "DE",
    italian: "IT",
    russian: "RU",
    spanish: "ES",
    czech: "CZ",
    dutch: "NL",
    polish: "PL",
    portuguese: "PT",
    romanian: "RO",
    slovenian: "SI",
    swedish: "SE",
    "portuguese-brazilian": "BR",
  };
  return languageToCountry[code] || "UN"; // Fallback to UN if no mapping
};

export const LanguagesSection = () => {
  const t = useTranslations("HomePage");
  const langT = useTranslations("LanguageNames");
  const locale = useLocale();
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

  // Transform SUPPORTED_LANGUAGES for the LanguageScroll and Modal
  const languageItems = SUPPORTED_LANGUAGES.map((lang) => ({
    id: lang.code,
    code: lang.code,
    name: langT(lang.code),
    nativeName: lang.nativeName,
    countryCode: getCountryCodeForLanguage(lang.code),
  }));

  // Sort languages based on locale
  const sortedLanguages = [...SUPPORTED_LANGUAGES];

  if (locale === "ja") {
    // For Japanese locale, prioritize these languages in order of importance for Japanese users
    const priorityLanguages = [
      "japanese",
      "english",
      "chinese-simplified",
      "chinese-traditional",
      "korean",
      "cantonese",
      "thai",
      "vietnamese",
      "indonesian",
      "filipino",
      "french",
      "german",
      "spanish",
      "italian",
      "russian",
    ];

    // First sort all languages alphabetically by translated name
    sortedLanguages.sort((a, b) => langT(a.code).localeCompare(langT(b.code)));

    // Then move priority languages to the top (in reverse order to maintain the desired sequence)
    priorityLanguages.reverse().forEach((langCode) => {
      const index = sortedLanguages.findIndex((lang) => lang.code === langCode);
      if (index !== -1) {
        const [lang] = sortedLanguages.splice(index, 1);
        sortedLanguages.unshift(lang);
      }
    });
  } else {
    // For English locale, sort alphabetically
    sortedLanguages.sort((a, b) => langT(a.code).localeCompare(langT(b.code)));
  }

  return (
    <section
      id="languages"
      className="container mx-auto px-4 py-16 md:py-24 border-t border-border relative"
      aria-labelledby="languages-heading"
    >
      {/* Background decorative elements */}
      <div
        className="absolute left-0 top-1/3 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl -z-10 opacity-30"
        aria-hidden="true"
      ></div>
      <div
        className="absolute right-0 bottom-1/4 w-80 h-80 bg-primary/10 rounded-full blur-3xl -z-10 opacity-30"
        aria-hidden="true"
      ></div>

      <div className="text-center mb-16">
        <h2
          id="languages-heading"
          className="text-3xl md:text-4xl font-display font-[650] tracking-tight mb-4"
        >
          {t("languagesSupported")}
        </h2>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-sans">
          {t("reachGlobalAudience")}
        </p>
        <div
          className="w-24 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent mx-auto mt-8"
          aria-hidden="true"
        ></div>
      </div>

      {/* Mobile View (with Language Scroll) */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="md:hidden"
      >
        <h3 className="sr-only">Languages list (scrollable)</h3>
        <LanguageScroll
          languages={languageItems}
          onShowAll={() => setIsLanguageModalOpen(true)}
        />

        <LanguageModal
          languages={languageItems}
          open={isLanguageModalOpen}
          onOpenChange={setIsLanguageModalOpen}
        />
      </motion.div>

      {/* Desktop View (always expanded) */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="hidden md:block max-w-7xl mx-auto"
      >
        <h3 className="sr-only">All supported languages</h3>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {sortedLanguages.map((lang, index) => {
            const countryCode = getCountryCodeForLanguage(lang.code);
            // Use dynamic import for flag components
            const FlagComponent = flags[countryCode as keyof typeof flags];

            return (
              <li key={lang.code}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="flex items-center gap-3 p-4 rounded-xl border border-border bg-background shadow-sm hover:border-primary/20 hover:shadow-md transition-all duration-300"
                >
                  {FlagComponent ? (
                    <FlagComponent
                      className="h-6 w-9 rounded shadow-sm"
                      aria-hidden="true"
                    />
                  ) : (
                    <div
                      className="h-6 w-9 flex items-center justify-center rounded-md bg-slate-100 text-slate-700"
                      aria-hidden="true"
                    >
                      <span className="text-sm font-medium">
                        {lang.nativeName.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{langT(lang.code)}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {lang.nativeName}
                    </p>
                  </div>
                </motion.div>
              </li>
            );
          })}
        </ul>
      </motion.div>

      <div className="mt-16 text-center">
        <p className="text-muted-foreground max-w-2xl mx-auto font-sans">
          {t("preserveContext")}
        </p>
        <p className="text-sm text-muted-foreground mt-4 max-w-2xl mx-auto font-sans">
          {t("moreLanguagesAdded")}
        </p>
      </div>
    </section>
  );
};

export default LanguagesSection;
